import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { MODALS, ModalProps } from './types';
import {
  modalOpen,
  modalClose,
  modalReset,
  modalResetExcluded,
  modalCloseAll,
} from '@/store/modal/actions';
import { selectModalIsOpen } from '@/store/modal/selectors';
import {
  pushModalHistoryEntry,
  cleanupModalHistoryEntry,
  cleanupAllModalHistoryEntries,
} from './modalHistoryState';

export const useModal = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectModalIsOpen);

  const openModal = useCallback(
    <T extends MODALS>(modal: T, props?: ModalProps) => {
      dispatch(modalOpen(modal, props));
      if (props?.pushHistory) {
        pushModalHistoryEntry(modal);
      }
    },
    [dispatch],
  );

  const closeModal = useCallback(
    (modal?: MODALS) => {
      if (modal !== undefined) {
        cleanupModalHistoryEntry(modal);
      }
      dispatch(modalClose(modal));
    },
    [dispatch],
  );

  const resetModals = useCallback(() => {
    cleanupAllModalHistoryEntries();
    dispatch(modalReset());
  }, [dispatch]);

  const resetModalsExcluded = useCallback(
    (excludedModals: MODALS[]) => {
      cleanupAllModalHistoryEntries();
      dispatch(modalResetExcluded(excludedModals));
    },
    [dispatch],
  );

  const closeAllModals = useCallback(
    (exceptions?: MODALS[]) => {
      cleanupAllModalHistoryEntries();
      dispatch(modalCloseAll(exceptions));
    },
    [dispatch],
  );

  return { isOpen, openModal, closeModal, resetModals, resetModalsExcluded, closeAllModals };
};
